/** Page de gestion des plannings types, paramétrée par métier.
 *
 * Affiche/édite uniquement les plannings types d'un métier donné (boulangerie,
 * traiteur, cleaning, driving...). L'éditeur de créneau s'enrichit selon le métier :
 * zones + EPI pour le nettoyage, tournée + véhicule pour les chauffeurs.
 *
 * Exporte aussi `ComparisonTab` (comparaison pointages vs planning), réutilisé
 * par la page de suivi des pointages.
 */

import { useState } from 'react';
import { addDays, addMonths, format, getISOWeek, startOfISOWeek } from 'date-fns';
import { parseServerDate } from '../lib/utils';
import { fr } from 'date-fns/locale';
import { CalendarDays, ChevronLeft, ChevronRight, Clock, Download, FileSpreadsheet, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { useEmployees } from '../hooks/useEmployees';
import { cleaningZones, epis, rounds, vehicles } from '../hooks/useArbreapains';

const API_URL = import.meta.env.VITE_API_URL;

async function downloadComparison(opts: {
  period: 'week' | 'month';
  start: string;
  fmt: 'csv' | 'pdf';
  employee_id?: number;
  site_id?: number;
}) {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams();
  params.set('period', opts.period);
  params.set('start', opts.start);
  params.set('fmt', opts.fmt);
  if (opts.employee_id) params.set('employee_id', String(opts.employee_id));
  if (opts.site_id) params.set('site_id', String(opts.site_id));
  const res = await fetch(`${API_URL}/api/reports/comparison?${params}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    alert('Erreur lors de la génération du rapport');
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const ext = opts.fmt === 'pdf' ? 'pdf' : 'csv';
  a.download = `comparaison-${opts.period}-${opts.start}.${ext}`;
  a.click();
  URL.revokeObjectURL(url);
}
import {
  useSchedules,
  useCreateSchedule,
  useUpdateSchedule,
  useDeleteSchedule,
  useWeeklyComparison,
  useMonthlyComparison,
  type ComparisonEntry,
  type DailyComparison,
  type MonthlyEmployeeEntry,
  type SlotData,
  type WorkSchedule,
} from '../hooks/useSchedules';
import { useSites } from '../hooks/useSites';

type Activity = 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office';

/** Titre de page selon le métier. */
const ACTIVITY_TITLE: Record<Activity, string> = {
  boulangerie: 'Plannings Boulangerie',
  traiteur: 'Plannings Traiteur',
  chambre_froide: 'Plannings Chambre froide',
  cleaning: 'Plannings Nettoyage',
  driving: 'Plannings Chauffeurs',
  office: 'Plannings Bureau',
};

const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const JOURS_LONGS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/** Palette de couleurs cyclique pour différencier visuellement les plannings */
const PALETTE = [
  { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300', bar: 'bg-indigo-500' },
  { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', bar: 'bg-emerald-500' },
  { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300', bar: 'bg-amber-500' },
  { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-300', bar: 'bg-rose-500' },
  { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-300', bar: 'bg-sky-500' },
  { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-300', bar: 'bg-violet-500' },
  { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300', bar: 'bg-teal-500' },
  { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', bar: 'bg-orange-500' },
] as const;

function colorFor(scheduleId: number | null | undefined) {
  if (!scheduleId) return null;
  return PALETTE[scheduleId % PALETTE.length];
}

/** Slots par défaut : lun-ven 8h-17h, sam-dim repos */
function defaultSlots(): SlotData[] {
  return Array.from({ length: 7 }, (_, i) => ({
    day_of_week: i,
    start_time: i < 5 ? '08:00' : '00:00',
    end_time: i < 5 ? '17:00' : '00:00',
    is_working: i < 5,
    zone_ids: [],
    epi_ids: [],
    round_id: null,
    vehicle_id: null,
  }));
}

/** Forme minimale (horaires) partagée entre SlotData et SlotResponse pour l'affichage.
 *  Horaires null (tournée sans horaires) : le créneau n'est ni compté ni tracé. */
type TimeSlot = {
  day_of_week: number;
  start_time: string | null;
  end_time: string | null;
  is_working: boolean;
};

/** Créneau réellement horodaté : seul cas affichable sur la timeline. */
type TimedSlot = TimeSlot & { start_time: string; end_time: string };
const isTimed = (s: TimeSlot): s is TimedSlot => !!s.start_time && !!s.end_time;

/** Convertit HH:MM en fraction de journée (0..1) pour positionner la barre */
function timeToRatio(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return Math.min(1, (h * 60 + m) / (24 * 60));
}

/** Durée hebdomadaire d'un planning en heures (somme des créneaux travaillés). */
function weeklyHours(slots: TimeSlot[]): number {
  let minutes = 0;
  for (const s of slots) {
    if (!s.is_working || !isTimed(s)) continue;
    const [hs, ms] = s.start_time.split(':').map(Number);
    const [he, me] = s.end_time.split(':').map(Number);
    let end = he * 60 + me;
    const start = hs * 60 + ms;
    if (end < start) end += 24 * 60; // créneau à cheval sur minuit (ex: 21:00 → 06:00)
    minutes += end - start;
  }
  return minutes / 60;
}

/** Formate un nombre d'heures : 35 → "35h", 37.5 → "37h30". */
function formatHours(hours: number): string {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m === 0 ? `${h}h` : `${h}h${m.toString().padStart(2, '0')}`;
}

// --- Barre timeline compacte d'un planning ---

function ScheduleTimeline({ slots, barClass }: { slots: TimeSlot[]; barClass: string }) {
  // Une ligne par jour, avec potentiellement plusieurs barres (coupures).
  return (
    <div className="space-y-1.5">
      {[0, 1, 2, 3, 4, 5, 6].map((day) => {
        const daySlots = slots
          .filter((s): s is TimedSlot => s.day_of_week === day && s.is_working && isTimed(s))
          .sort((a, b) => a.start_time.localeCompare(b.start_time));
        const isWorking = daySlots.length > 0;
        return (
          <div key={day} className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-gray-500 w-8">
              {JOURS[day]}
            </span>
            <div className="relative flex-1 h-3 rounded bg-gray-100 overflow-hidden">
              {daySlots.map((slot, i) => {
                const startRatio = timeToRatio(slot.start_time);
                const endRatio = timeToRatio(slot.end_time);
                // Créneau à cheval sur minuit : deux barres (début→minuit, minuit→fin)
                const segments =
                  endRatio < startRatio
                    ? [
                        { left: startRatio, width: 1 - startRatio },
                        { left: 0, width: endRatio },
                      ]
                    : [{ left: startRatio, width: Math.max(0, endRatio - startRatio) }];
                return segments.map((seg, j) => (
                  <div
                    key={`${i}-${j}`}
                    className={`absolute top-0 h-full rounded ${barClass}`}
                    style={{ left: `${seg.left * 100}%`, width: `${seg.width * 100}%` }}
                  />
                ));
              })}
            </div>
            <span className="text-[11px] text-gray-500 w-32 text-right tabular-nums">
              {isWorking
                ? daySlots.map((s) => `${s.start_time}–${s.end_time}`).join(' · ')
                : 'Repos'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// --- Formulaire créneaux (réutilisé création + édition) ---

interface SlotEditorProps {
  slots: SlotData[];
  onChange: (slots: SlotData[]) => void;
  activity: Activity;
  zones: { id: number; name: string }[];
  episList: { id: number; name: string }[];
  roundsList: { id: number; name: string }[];
  vehiclesList: { id: number; name: string }[];
}

/** Créneau neuf travaillé, avec les champs métier initialisés vides. */
function newWorkingSlot(day: number, start: string, end: string): SlotData {
  return {
    day_of_week: day,
    start_time: start,
    end_time: end,
    is_working: true,
    zone_ids: [],
    epi_ids: [],
    round_id: null,
    vehicle_id: null,
  };
}

/** Marqueur "Repos" pour un jour non travaillé. */
function restSlot(day: number): SlotData {
  return {
    day_of_week: day,
    start_time: '00:00',
    end_time: '00:00',
    is_working: false,
    zone_ids: [],
    epi_ids: [],
    round_id: null,
    vehicle_id: null,
  };
}

function SlotEditor({
  slots,
  onChange,
  activity,
  zones,
  episList,
  roundsList,
  vehiclesList,
}: SlotEditorProps) {
  // Multi-slots par jour : on regroupe les slots `is_working=true` par day_of_week.
  const slotsByDay = (day: number) =>
    slots
      .map((s, idx) => ({ s, idx }))
      .filter((it) => it.s.day_of_week === day && it.s.is_working);

  const dayHasRest = (day: number) =>
    slots.some((s) => s.day_of_week === day && !s.is_working);

  const setDayWorking = (day: number, working: boolean) => {
    const next = slots.filter((s) => s.day_of_week !== day);
    next.push(working ? newWorkingSlot(day, '08:00', '17:00') : restSlot(day));
    onChange(next);
  };

  const addSlot = (day: number) => {
    const existing = slotsByDay(day);
    const last = existing[existing.length - 1]?.s;
    onChange([...slots, newWorkingSlot(day, last ? last.end_time : '14:00', '17:00')]);
  };

  const removeSlot = (idx: number) => {
    const day = slots[idx].day_of_week;
    const next = slots.filter((_, i) => i !== idx);
    // S'il ne reste plus de plage travaillée pour ce jour, on remet un marqueur Repos
    if (!next.some((s) => s.day_of_week === day && s.is_working)) {
      next.push(restSlot(day));
    }
    onChange(next);
  };

  const updateSlot = (idx: number, patch: Partial<SlotData>) => {
    onChange(slots.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-stone-500 italic">
        Horaires de nuit : saisissez une fin avant le début (ex&nbsp;: 21:00 → 06:00),
        le créneau se termine le lendemain.
      </p>
      {[0, 1, 2, 3, 4, 5, 6].map((day) => {
        const daySlots = slotsByDay(day);
        const isWorking = daySlots.length > 0;
        return (
          <div key={day} className="border border-stone-100 rounded-md p-2">
            <label className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isWorking}
                  onChange={(e) => setDayWorking(day, e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-xs font-medium text-gray-700 w-20">
                  {JOURS_LONGS[day]}
                </span>
              </span>
              {isWorking && (
                <button
                  type="button"
                  onClick={() => addSlot(day)}
                  className="text-[11px] text-accent hover:underline"
                >
                  + Ajouter une plage
                </button>
              )}
            </label>
            {!isWorking && dayHasRest(day) ? (
              <div className="text-xs text-gray-400 italic ml-6 mt-1">Repos</div>
            ) : (
              <div className="space-y-2 mt-1.5 ml-6">
                {daySlots.map(({ s, idx }) => (
                  <div key={idx} className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        value={s.start_time}
                        onChange={(e) => updateSlot(idx, { start_time: e.target.value })}
                        className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                      />
                      <span className="text-gray-400">→</span>
                      <input
                        type="time"
                        value={s.end_time}
                        onChange={(e) => updateSlot(idx, { end_time: e.target.value })}
                        className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                      />
                      {daySlots.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSlot(idx)}
                          className="text-[11px] text-stone-400 hover:text-rose-600"
                          title="Supprimer cette plage"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* Spécificités métier portées par créneau */}
                    {activity === 'cleaning' && (
                      <div className="space-y-1.5">
                        <CheckboxGrid
                          label="Zones"
                          items={zones}
                          selected={s.zone_ids}
                          emptyHint="Aucune zone configurée."
                          onToggle={(id) =>
                            updateSlot(idx, { zone_ids: toggle(s.zone_ids, id) })
                          }
                        />
                        <CheckboxGrid
                          label="EPI"
                          items={episList}
                          selected={s.epi_ids}
                          emptyHint="Aucun EPI configuré."
                          onToggle={(id) =>
                            updateSlot(idx, { epi_ids: toggle(s.epi_ids, id) })
                          }
                        />
                      </div>
                    )}
                    {activity === 'driving' && (
                      <div className="flex flex-wrap gap-2">
                        <RefSelect
                          label="Tournée"
                          items={roundsList}
                          value={s.round_id}
                          onChange={(v) => updateSlot(idx, { round_id: v })}
                        />
                        <RefSelect
                          label="Véhicule"
                          items={vehiclesList}
                          value={s.vehicle_id}
                          onChange={(v) => updateSlot(idx, { vehicle_id: v })}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Bascule un id dans/hors d'un tableau de sélection. */
function toggle(set: number[], id: number): number[] {
  return set.includes(id) ? set.filter((i) => i !== id) : [...set, id];
}

/** Grille de cases à cocher (réutilise le style de CellEditor de la page nettoyage). */
function CheckboxGrid({
  label,
  items,
  selected,
  emptyHint,
  onToggle,
}: {
  label: string;
  items: { id: number; name: string }[];
  selected: number[];
  emptyHint: string;
  onToggle: (id: number) => void;
}) {
  return (
    <div>
      <p className="text-[11px] font-medium text-stone-600 mb-1">
        {label} ({selected.length})
      </p>
      <div className="grid grid-cols-2 gap-1 max-h-32 overflow-y-auto border border-stone-200 rounded-md p-2">
        {items.length === 0 && (
          <p className="col-span-2 text-[11px] text-stone-400 italic p-1">{emptyHint}</p>
        )}
        {items.map((it) => (
          <label
            key={it.id}
            className="flex items-center gap-2 px-2 py-1 hover:bg-stone-50 rounded cursor-pointer text-xs"
          >
            <input
              type="checkbox"
              checked={selected.includes(it.id)}
              onChange={() => onToggle(it.id)}
              className="rounded"
            />
            {it.name}
          </label>
        ))}
      </div>
    </div>
  );
}

/** Sélecteur d'une référence unique (tournée ou véhicule). */
function RefSelect({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { id: number; name: string }[];
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-medium text-stone-600">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        className="rounded-md border border-stone-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-accent"
      >
        <option value="">— Aucun —</option>
        {items.map((it) => (
          <option key={it.id} value={it.id}>
            {it.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// --- Carte d'un planning avec actions ---

function ScheduleCard({
  schedule,
  onEdit,
  onDelete,
}: {
  schedule: WorkSchedule;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const color = colorFor(schedule.id)!;
  const nbWorking = schedule.slots.filter((s) => s.is_working).length;
  const hours = weeklyHours(schedule.slots);

  return (
    <div className={`rounded-lg border ${color.border} bg-white shadow-sm overflow-hidden`}>
      <div className={`${color.bg} px-4 py-2.5 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${color.bar}`} />
          <h3 className={`text-sm font-semibold ${color.text}`}>{schedule.name}</h3>
          <span className={`text-[11px] ${color.text} opacity-75`}>
            · {nbWorking} j · <span className="num font-semibold">{formatHours(hours)}/sem</span>
          </span>
        </div>
        <div className="flex gap-1">
          <button
            onClick={onEdit}
            className={`p-1 rounded hover:bg-white/50 ${color.text}`}
            title="Modifier"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onDelete}
            className="p-1 rounded hover:bg-white/50 text-gray-500 hover:text-red-600"
            title="Supprimer"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
      <div className="p-4">
        <ScheduleTimeline slots={schedule.slots} barClass={color.bar} />
      </div>
    </div>
  );
}

// --- Page principale (paramétrée par métier) ---

/** Convertit un SlotResponse (refs embarquées) en SlotData (ids) pour l'éditeur. */
function slotResponseToData(s: WorkSchedule['slots'][number]): SlotData {
  return {
    day_of_week: s.day_of_week,
    // L'éditeur exige des horaires : un créneau sans horaires part d'un champ vide
    start_time: s.start_time ?? '',
    end_time: s.end_time ?? '',
    is_working: s.is_working,
    zone_ids: s.zones.map((z) => z.id),
    epi_ids: s.epis.map((e) => e.id),
    round_id: s.round?.id ?? null,
    vehicle_id: s.vehicle?.id ?? null,
  };
}

export function SchedulesPage({ activity }: { activity: Activity }) {
  // --- Données ---
  const { data: schedules, isLoading: schedulesLoading } = useSchedules(activity);
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  // Référentiels métier (utilisés selon l'activité dans l'éditeur de créneau)
  const { data: zones } = cleaningZones.useList();
  const { data: episList } = epis.useList();
  const { data: roundsList } = rounds.useList();
  const { data: vehiclesList } = vehicles.useList();

  // --- Modale création/édition planning ---
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editorName, setEditorName] = useState('');
  const [editorSlots, setEditorSlots] = useState<SlotData[]>(defaultSlots);
  const [nameError, setNameError] = useState<string | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setEditorName('');
    setEditorSlots(defaultSlots());
    setNameError(null);
    setEditorOpen(true);
  };

  const openEdit = (schedule: WorkSchedule) => {
    setEditingId(schedule.id);
    setEditorName(schedule.name);
    setEditorSlots(schedule.slots.map(slotResponseToData));
    setNameError(null);
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!editorName.trim()) {
      setNameError('Le nom du planning est requis');
      return;
    }
    if (editingId) {
      await updateSchedule.mutateAsync({ id: editingId, name: editorName, slots: editorSlots });
    } else {
      await createSchedule.mutateAsync({ name: editorName, activity, slots: editorSlots });
    }
    setEditorOpen(false);
  };

  const handleDelete = async (schedule: WorkSchedule) => {
    if (!confirm(`Supprimer le planning "${schedule.name}" ?`)) return;
    await deleteSchedule.mutateAsync(schedule.id);
  };

  return (
    <div>
      <Header
        title={ACTIVITY_TITLE[activity]}
        action={
          <Button size="sm" onClick={openCreate}>
            <Plus size={14} className="mr-1" /> Nouveau planning
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {schedulesLoading ? (
          <p className="text-sm text-gray-400">Chargement...</p>
        ) : schedules && schedules.length > 0 ? (
          schedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              onEdit={() => openEdit(schedule)}
              onDelete={() => handleDelete(schedule)}
            />
          ))
        ) : (
          <p className="text-sm text-gray-400">Aucun planning. Créez-en un pour commencer.</p>
        )}
      </div>

      {/* Modale création/édition */}
      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editingId ? 'Modifier le planning' : 'Nouveau planning'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              loading={createSchedule.isPending || updateSchedule.isPending}
            >
              {editingId ? 'Enregistrer' : 'Créer'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {/* Champ nom collant : reste visible même quand on scrolle les jours */}
          <div className="sticky top-0 z-10 bg-white pb-2">
            <Input
              label="Nom du planning"
              value={editorName}
              onChange={(e) => {
                setEditorName(e.target.value);
                if (nameError) setNameError(null);
              }}
              error={nameError ?? undefined}
              placeholder="Ex: Standard 8h-17h, Mi-temps matin, Intérim..."
            />
          </div>
          <SlotEditor
            slots={editorSlots}
            onChange={setEditorSlots}
            activity={activity}
            zones={zones ?? []}
            episList={episList ?? []}
            roundsList={roundsList ?? []}
            vehiclesList={vehiclesList ?? []}
          />
        </div>
      </Modal>
    </div>
  );
}

// --- Onglet comparaison pointages vs planning ---

const MONTH_LABELS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function formatHoursCompact(h: number): string {
  if (h === 0) return '0h';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  if (mm === 0) return `${hh}h`;
  return `${hh}h${mm.toString().padStart(2, '0')}`;
}

/** Classe couleur selon l'écart réalisé - prévu (heures). */
function diffClass(actual: number, planned: number): { cell: string; label: string } {
  if (planned === 0 && actual === 0) return { cell: 'bg-stone-50 text-stone-400', label: '' };
  const diffMin = (actual - planned) * 60;
  if (diffMin >= -15) return { cell: 'bg-emerald-50 text-emerald-800 border-emerald-100', label: 'ok' };
  if (diffMin >= -60) return { cell: 'bg-amber-50 text-amber-800 border-amber-200', label: 'warn' };
  return { cell: 'bg-rose-50 text-rose-800 border-rose-200', label: 'bad' };
}

function SummaryBlock({
  label,
  planned,
  actual,
  variant = 'default',
}: {
  label: string;
  planned: number;
  actual: number;
  variant?: 'default' | 'highlight';
}) {
  const diff = actual - planned;
  const pct = planned > 0 ? (actual / planned) * 100 : 0;
  const bar =
    diff >= -0.25 ? 'bg-emerald-500' : diff >= -1 ? 'bg-amber-500' : 'bg-rose-500';
  const barBg = variant === 'highlight' ? 'bg-white/30' : 'bg-stone-100';
  const textColor = variant === 'highlight' ? 'text-white' : 'text-ink';
  const subColor = variant === 'highlight' ? 'text-stone-300' : 'text-stone-500';
  const diffColor = variant === 'highlight'
    ? (diff >= -0.25 ? 'text-emerald-300' : diff >= -1 ? 'text-amber-300' : 'text-rose-300')
    : (diff >= -0.25 ? 'text-emerald-700' : diff >= -1 ? 'text-amber-700' : 'text-rose-700');
  return (
    <div
      className={`rounded-lg p-3 ${
        variant === 'highlight'
          ? 'bg-sidebar text-white'
          : 'bg-white border border-stone-200'
      }`}
    >
      <p className={`text-[11px] uppercase tracking-widest font-semibold ${subColor}`}>
        {label}
      </p>
      <div className={`flex items-baseline gap-2 mt-1 ${textColor}`}>
        <span className="num text-xl font-bold">{formatHoursCompact(actual)}</span>
        <span className={`text-xs ${subColor}`}>/ {formatHoursCompact(planned)}</span>
      </div>
      <div className={`h-1.5 ${barBg} rounded-full overflow-hidden mt-2`}>
        <div
          className={`h-full ${bar} rounded-full transition-all`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
      <p className={`text-[11px] mt-1.5 font-medium ${diffColor}`}>
        {diff >= 0 ? '▲ +' : '▼ '}
        {formatHoursCompact(Math.abs(diff))}
      </p>
    </div>
  );
}

function ComparisonRow({ entry }: { entry: ComparisonEntry }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="px-5 py-3 flex items-center justify-between border-b border-stone-100 bg-stone-50/60">
        <div>
          <p className="font-semibold text-ink">{entry.employee_name}</p>
          <p className="text-[11px] text-stone-500">
            {entry.site_name ?? '—'}
            {entry.schedule_name ? (
              <>
                {' · '}
                <span className="text-accent font-medium">{entry.schedule_name}</span>
              </>
            ) : (
              <span className="text-rose-600 font-medium">
                {' · '}Aucun planning cette semaine
              </span>
            )}
          </p>
        </div>
      </div>
      {entry.schedule_name ? (
        <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4">
          {/* 7 cases jour — scroll horizontal sur mobile avec min-width pour lisibilité */}
          <div className="overflow-x-auto -mx-1 px-1">
            <div className="grid grid-cols-7 gap-1.5 min-w-[640px]">
            {entry.days.map((d) => {
              const dt = new Date(d.date + 'T00:00');
              const dow = JOURS[dt.getDay() === 0 ? 6 : dt.getDay() - 1];
              const dayNum = dt.getDate();
              const { cell } = diffClass(d.actual_hours, d.planned_hours);
              const fmtHM = (iso: string | null) =>
                iso ? format(parseServerDate(iso), 'HH:mm') : '—';
              const fmtRange = (start: string | null, end: string | null) => {
                // Multi-segments si présents (coupures), sinon fallback start/end
                if (d.planned_segments && d.planned_segments.length > 0) {
                  return d.planned_segments.map((s) => `${s.start}–${s.end}`).join(' · ');
                }
                return start && end ? `${start}–${end}` : null;
              };
              return (
                <div
                  key={d.date}
                  className={`rounded-md border px-2 py-2 text-center ${cell}`}
                >
                  <p className="text-[10px] uppercase font-semibold opacity-75">
                    {dow} {dayNum}
                  </p>
                  {d.is_absent ? (
                    <p className="text-[11px] italic font-medium mt-1">Absent</p>
                  ) : d.planned_hours === 0 && d.actual_hours === 0 ? (
                    /* Jour non travaillé selon le planning et aucun pointage : on n'affiche rien */
                    <p className="text-[11px] text-stone-400 italic mt-2">Repos</p>
                  ) : (
                    <>
                      {/* Ligne 1 : réalisé (bold, terracotta) + prévu (indigo clair) */}
                      <p className="num mt-1 flex items-center justify-center gap-1 leading-tight">
                        <Clock size={11} className="text-accent" />
                        <span className="text-base font-bold text-accent">
                          {formatHoursCompact(d.actual_hours)}
                        </span>
                        <span className="text-stone-300">/</span>
                        <CalendarDays size={11} className="text-indigo-500" />
                        <span className="text-[11px] font-medium text-indigo-600">
                          {formatHoursCompact(d.planned_hours)}
                        </span>
                      </p>
                      {/* Ligne 2 : horaire prévu (indigo) */}
                      <p
                        className="num text-[10px] font-medium mt-1 flex items-center justify-center gap-1 text-indigo-600"
                        title="Planning"
                      >
                        <CalendarDays size={10} />
                        {fmtRange(d.planned_start, d.planned_end) ?? '—'}
                      </p>
                      {/* Ligne 3 : pointages effectifs (terracotta, bold) */}
                      <p
                        className="num text-[10px] font-bold flex items-center justify-center gap-1 text-accent"
                        title="Pointages effectifs"
                      >
                        <Clock size={10} />
                        {d.actual_in || d.actual_out
                          ? `${fmtHM(d.actual_in)}–${fmtHM(d.actual_out)}`
                          : 'Pas pointé'}
                      </p>
                    </>
                  )}
                </div>
              );
            })}
            </div>
          </div>
          <SummaryBlock
            label="Semaine"
            planned={entry.week_planned}
            actual={entry.week_actual}
          />
          <SummaryBlock
            label="Mois"
            planned={entry.month_planned}
            actual={entry.month_actual}
            variant="highlight"
          />
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-sm text-rose-700">
            Impossible de comparer : aucun planning attribué à cet employé pour cette semaine.
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Affecte un planning via l'onglet <span className="font-medium">Vue hebdomadaire</span>.
          </p>
        </div>
      )}
    </div>
  );
}

export function ComparisonTab({ showActivityFilter = false }: { showActivityFilter?: boolean } = {}) {
  const [view, setView] = useState<'week' | 'month'>('week');
  const [weekStart, setWeekStart] = useState<Date>(() => startOfISOWeek(new Date()));
  const [monthCursor, setMonthCursor] = useState<Date>(() => new Date());
  const [siteFilter, setSiteFilter] = useState<number | undefined>();
  const [activityFilter, setActivityFilter] = useState<
    '' | 'boulangerie' | 'traiteur' | 'chambre_froide' | 'cleaning' | 'driving' | 'office'
  >('');
  const [exportOpen, setExportOpen] = useState(false);

  const { data: sites } = useSites();

  const weekStartIso = format(weekStart, 'yyyy-MM-dd');
  const monthIso = format(monthCursor, 'yyyy-MM');
  const weekEnd = addDays(weekStart, 6);

  const { data: weekData, isLoading: weekLoading } = useWeeklyComparison(
    view === 'week' ? weekStartIso : '',
    siteFilter,
    activityFilter || undefined,
  );
  const { data: monthData, isLoading: monthLoading } = useMonthlyComparison(
    view === 'month' ? monthIso : '',
    siteFilter,
    activityFilter || undefined,
  );

  const shiftWeek = (days: number) => setWeekStart((d) => addDays(d, days));
  const shiftMonth = (n: number) => setMonthCursor((d) => addMonths(d, n));
  const goToday = () => {
    setWeekStart(startOfISOWeek(new Date()));
    setMonthCursor(new Date());
  };

  const headerTitle = view === 'week'
    ? `Semaine ${getISOWeek(weekStart)} · du ${format(weekStart, 'd MMM', { locale: fr })} au ${format(weekEnd, 'd MMM yyyy', { locale: fr })}`
    : `${MONTH_LABELS[monthCursor.getMonth()]} ${monthCursor.getFullYear()}`;

  return (
    <div className="space-y-4">
      <Card
        title={headerTitle}
        action={
          <div className="flex flex-wrap items-center gap-2">
            {/* Toggle Semaine / Mois */}
            <div className="flex p-0.5 bg-stone-100 rounded-lg text-xs">
              <button
                onClick={() => setView('week')}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${
                  view === 'week' ? 'bg-white shadow-sm text-ink' : 'text-stone-500'
                }`}
              >
                Semaine
              </button>
              <button
                onClick={() => setView('month')}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${
                  view === 'month' ? 'bg-white shadow-sm text-ink' : 'text-stone-500'
                }`}
              >
                Mois
              </button>
            </div>
            <select
              value={siteFilter ?? ''}
              onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : undefined)}
              className="rounded-md border border-stone-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <option value="">Tous les sites</option>
              {sites?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {showActivityFilter && (
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value as typeof activityFilter)}
                className="rounded-md border border-stone-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="">Toutes activités</option>
                <option value="boulangerie">Boulangerie</option>
                <option value="traiteur">Traiteur</option>
                <option value="chambre_froide">Chambre froide</option>
                <option value="cleaning">Nettoyage</option>
                <option value="driving">Chauffeurs</option>
                <option value="office">Bureau</option>
              </select>
            )}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (view === 'week' ? shiftWeek(-7) : shiftMonth(-1))}
              >
                <ChevronLeft size={14} />
              </Button>
              <Button variant="secondary" size="sm" onClick={goToday}>
                Aujourd'hui
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => (view === 'week' ? shiftWeek(7) : shiftMonth(1))}
              >
                <ChevronRight size={14} />
              </Button>
            </div>
            <Button variant="dark" size="sm" onClick={() => setExportOpen(true)}>
              <Download size={13} className="mr-1" /> Exporter
            </Button>
          </div>
        }
      >
        <div className="flex flex-wrap items-center gap-3 text-[11px] text-stone-500">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-emerald-200 border border-emerald-300" />
            Écart ≤ 15 min
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-amber-200 border border-amber-300" />
            Écart 15–60 min
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-rose-200 border border-rose-300" />
            Écart &gt; 60 min
          </span>
          <span className="ml-auto italic">Affichage : réalisé / prévu</span>
        </div>
      </Card>

      {view === 'week' ? (
        weekLoading ? (
          <Card><p className="text-sm text-stone-400">Chargement...</p></Card>
        ) : !weekData || weekData.entries.length === 0 ? (
          <Card><p className="text-sm text-stone-400">Aucun employé.</p></Card>
        ) : (
          <div className="space-y-3">
            {weekData.entries.map((e) => (
              <ComparisonRow key={e.employee_id} entry={e} />
            ))}
          </div>
        )
      ) : monthLoading ? (
        <Card><p className="text-sm text-stone-400">Chargement...</p></Card>
      ) : !monthData || monthData.entries.length === 0 ? (
        <Card><p className="text-sm text-stone-400">Aucun employé.</p></Card>
      ) : (
        <div className="space-y-3">
          {monthData.entries.map((e) => (
            <MonthlyEmployeeCard key={e.employee_id} entry={e} />
          ))}
        </div>
      )}

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        defaultPeriod={view}
        defaultStart={view === 'week' ? weekStartIso : monthIso}
        siteId={siteFilter}
      />
    </div>
  );
}

// --- Modale d'export ---

function ExportModal({
  open,
  onClose,
  defaultPeriod,
  defaultStart,
  siteId,
}: {
  open: boolean;
  onClose: () => void;
  defaultPeriod: 'week' | 'month';
  defaultStart: string;
  siteId?: number;
}) {
  const [period, setPeriod] = useState<'week' | 'month'>(defaultPeriod);
  const [start, setStart] = useState(defaultStart);
  const [employeeId, setEmployeeId] = useState<string>('');
  const [fmt, setFmt] = useState<'csv' | 'pdf'>('pdf');
  const [loading, setLoading] = useState(false);

  // Re-sync à l'ouverture si la sélection courante a changé
  useState(() => {
    setPeriod(defaultPeriod);
    setStart(defaultStart);
  });

  const { data: employees } = useEmployees({ per_page: 100, active_only: true });

  const handleExport = async () => {
    setLoading(true);
    await downloadComparison({
      period,
      start,
      fmt,
      employee_id: employeeId ? Number(employeeId) : undefined,
      site_id: siteId,
    });
    setLoading(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Exporter la comparaison"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button onClick={handleExport} loading={loading}>
            <Download size={13} className="mr-1" /> Télécharger
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Période */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Période</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setPeriod('week');
                // Convertit YYYY-MM → 1er lundi du mois
                if (/^\d{4}-\d{2}$/.test(start)) {
                  const [y, m] = start.split('-').map(Number);
                  setStart(format(startOfISOWeek(new Date(y, m - 1, 15)), 'yyyy-MM-dd'));
                }
              }}
              className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                period === 'week'
                  ? 'bg-accent-soft border-accent text-accent'
                  : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
              }`}
            >
              Semaine
            </button>
            <button
              type="button"
              onClick={() => {
                setPeriod('month');
                if (/^\d{4}-\d{2}-\d{2}$/.test(start)) {
                  setStart(start.slice(0, 7));
                }
              }}
              className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                period === 'month'
                  ? 'bg-accent-soft border-accent text-accent'
                  : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
              }`}
            >
              Mois
            </button>
          </div>
        </div>

        {/* Start — date pour semaine, dropdown mois+année pour mois */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">
            {period === 'week' ? 'Lundi de la semaine' : 'Mois'}
          </label>
          {period === 'week' ? (
            <input
              type="date"
              value={/^\d{4}-\d{2}-\d{2}$/.test(start) ? start : ''}
              onChange={(e) => setStart(e.target.value)}
              className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
            />
          ) : (
            <MonthYearPicker
              value={/^\d{4}-\d{2}$/.test(start) ? start : format(new Date(), 'yyyy-MM')}
              onChange={setStart}
            />
          )}
        </div>

        {/* Employé */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Employé</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            className="w-full rounded-md border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <option value="">Tous les employés</option>
            {employees?.items.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>
        </div>

        {/* Format */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1.5">Format</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFmt('pdf')}
              className={`flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-lg border transition-colors ${
                fmt === 'pdf'
                  ? 'bg-accent-soft border-accent text-accent'
                  : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
              }`}
            >
              <FileText size={20} />
              <span className="text-xs font-medium">PDF</span>
              <span className="text-[10px] opacity-70">Mise en page soignée</span>
            </button>
            <button
              type="button"
              onClick={() => setFmt('csv')}
              className={`flex-1 flex flex-col items-center gap-1 px-3 py-3 rounded-lg border transition-colors ${
                fmt === 'csv'
                  ? 'bg-accent-soft border-accent text-accent'
                  : 'bg-white border-stone-300 text-stone-600 hover:border-stone-400'
              }`}
            >
              <FileSpreadsheet size={20} />
              <span className="text-xs font-medium">CSV (Excel)</span>
              <span className="text-[10px] opacity-70">Données brutes</span>
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// --- Carte calendrier mensuel par employé ---

function MonthlyEmployeeCard({ entry }: { entry: MonthlyEmployeeEntry }) {
  // Décalage pour aligner le 1er du mois sur sa colonne (lundi=0..dimanche=6)
  const firstDate = new Date(entry.days[0].date + 'T00:00');
  const firstWeekday = (firstDate.getDay() + 6) % 7; // dimanche=0 → 6
  const blanks = Array.from({ length: firstWeekday });

  const diff = entry.month_actual - entry.month_planned;
  const pct = entry.month_planned > 0 ? (entry.month_actual / entry.month_planned) * 100 : 0;
  const diffColor =
    diff >= -0.25
      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : diff >= -1
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-rose-700 bg-rose-50 border-rose-200';

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-stone-100 flex items-center justify-between bg-stone-50/60">
        <div>
          <p className="font-semibold text-ink">{entry.employee_name}</p>
          <p className="text-[11px] text-stone-500">
            {entry.site_name ?? '—'}
            {entry.schedule_name && (
              <>
                {' · '}
                <span className="text-accent font-medium">{entry.schedule_name}</span>
              </>
            )}
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${diffColor}`}>
          <span className="num text-base">{formatHoursCompact(entry.month_actual)}</span>
          <span className="opacity-70 ml-1">/ {formatHoursCompact(entry.month_planned)}</span>
          <span className="ml-2 opacity-90">
            ({diff >= 0 ? '+' : '−'}
            {formatHoursCompact(Math.abs(diff))})
          </span>
          <span className="ml-1 opacity-60">· {pct.toFixed(0)}%</span>
        </div>
      </div>
      <div className="p-4 overflow-x-auto">
        {/* Calendrier : largeur mini pour que 7 colonnes restent lisibles en mobile */}
        <div className="min-w-[700px]">
          {/* En-tête L M M J V S D */}
          <div className="grid grid-cols-7 gap-1 mb-1.5">
            {JOURS.map((j) => (
              <div key={j} className="text-center text-[10px] uppercase font-semibold text-stone-500">
                {j}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {blanks.map((_, i) => (
              <div key={`blank-${i}`} />
            ))}
            {entry.days.map((d) => (
              <MonthDayCell key={d.date} day={d} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function MonthDayCell({ day }: { day: DailyComparison }) {
  const dt = new Date(day.date + 'T00:00');
  const dayNum = dt.getDate();
  const isWorkDay = day.planned_hours > 0 || day.actual_hours > 0;
  const { cell } = diffClass(day.actual_hours, day.planned_hours);

  const fmtHM = (iso: string | null) =>
    iso ? format(parseServerDate(iso), 'HH:mm') : '—';
  const fmtRange = (start: string | null, end: string | null) => {
    if (day.planned_segments && day.planned_segments.length > 0) {
      return day.planned_segments.map((s) => `${s.start}–${s.end}`).join(' · ');
    }
    return start && end ? `${start}–${end}` : null;
  };

  if (day.is_absent) {
    return (
      <div className="min-h-[90px] rounded-md border border-stone-200 bg-stone-100 p-1.5 flex flex-col">
        <p className="text-[10px] font-semibold text-stone-500 text-right">{dayNum}</p>
        <p className="text-[10px] italic text-stone-500 text-center mt-2">Absent</p>
      </div>
    );
  }
  if (!isWorkDay) {
    return (
      <div className="min-h-[90px] rounded-md border border-stone-100 bg-stone-50 p-1.5 flex flex-col">
        <p className="text-[10px] text-stone-400 text-right">{dayNum}</p>
      </div>
    );
  }

  return (
    <div
      className={`min-h-[90px] rounded-md border p-1.5 flex flex-col gap-0.5 ${cell}`}
      title={`${day.actual_hours.toFixed(2)}h réalisées / ${day.planned_hours.toFixed(2)}h prévues`}
    >
      <p className="text-[10px] font-semibold text-right opacity-70">{dayNum}</p>
      {/* Ligne 1 : réalisé / prévu (heures) */}
      <p className="num text-center leading-tight">
        <Clock size={9} className="inline text-accent -mt-0.5" />
        <span className="text-[12px] font-bold text-accent ml-0.5">
          {formatHoursCompact(day.actual_hours)}
        </span>
        <span className="text-stone-300 mx-0.5">/</span>
        <CalendarDays size={9} className="inline text-indigo-500 -mt-0.5" />
        <span className="text-[10px] text-indigo-600 ml-0.5">
          {formatHoursCompact(day.planned_hours)}
        </span>
      </p>
      {/* Ligne 2 : horaire prévu */}
      <p className="num text-[9px] text-indigo-600 text-center flex items-center justify-center gap-0.5">
        <CalendarDays size={8} />
        {fmtRange(day.planned_start, day.planned_end) ?? '—'}
      </p>
      {/* Ligne 3 : pointages effectifs */}
      <p className="num text-[9px] font-bold text-accent text-center flex items-center justify-center gap-0.5">
        <Clock size={8} />
        {day.actual_in || day.actual_out
          ? `${fmtHM(day.actual_in)}–${fmtHM(day.actual_out)}`
          : '—'}
      </p>
    </div>
  );
}

// --- Sélecteur Mois + Année (deux dropdowns) ---

const MOIS_PICKER = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

function MonthYearPicker({
  value,
  onChange,
}: {
  value: string; // "YYYY-MM"
  onChange: (v: string) => void;
}) {
  const [year, month] = value.split('-').map(Number);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const update = (y: number, m: number) => {
    onChange(`${y}-${m.toString().padStart(2, '0')}`);
  };

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        value={month}
        onChange={(e) => update(year, Number(e.target.value))}
        className="rounded-md border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {MOIS_PICKER.map((label, i) => (
          <option key={i + 1} value={i + 1}>
            {label}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => update(Number(e.target.value), month)}
        className="rounded-md border border-stone-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}
